import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { HiOutlineChevronLeft } from "react-icons/hi";
import { fetchBookSummary, fetchBookSummarySource } from "../services/api";
import { useTheme } from "../context/ThemeContext";

const resolveChapterId = (chapter, index) => chapter?.chapter_id || `chapter-${index}`;
const resolveSectionId = (section, index) => section?.section_id || `section-${index}`;

const getChapterHeading = (chapterInfo = {}) => {
  const chapterNumber = chapterInfo.chapter_number || "";
  const chapterTitle = chapterInfo.chapter_title || "";

  if (chapterNumber && chapterTitle && chapterNumber !== chapterTitle) {
    return `${chapterNumber} - ${chapterTitle}`;
  }
  return chapterTitle || chapterNumber || "Untitled Chapter";
};

const SummaryList = ({ title, items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--foreground-muted)" }}>{title}</p>
      <ul className="list-disc pl-5 space-y-1.5 text-base leading-7" style={{ color: "var(--foreground)" }}>
        {items.map((item, index) => (
          <li key={`${title}-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

const resetSourceView = {
  open: false,
  loading: false,
  error: "",
  data: null,
  chapterTitle: "",
  sectionTitle: "",
};

const ListPaneButton = ({ isActive, onClick, children }) => (
  <button
    onClick={onClick}
    className="w-full text-left rounded-xl px-3.5 py-3 transition-colors border"
    style={
      isActive
        ? { borderColor: "color-mix(in srgb, var(--accent) 40%, transparent)", background: "var(--accent-tint)", color: "var(--accent)" }
        : { borderColor: "transparent", color: "var(--foreground)" }
    }
    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "var(--background-subtle)"; }}
    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
  >
    {children}
  </button>
);

const Summary = () => {
  const { readingKey } = useParams();
  const { mode } = useTheme();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeChapterId, setActiveChapterId] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState(null);

  const [sourceView, setSourceView] = useState(resetSourceView);

  useEffect(() => {
    let isCancelled = false;

    const loadSummary = async () => {
      setLoading(true);
      setError("");
      setSourceView(resetSourceView);

      try {
        const data = await fetchBookSummary(readingKey);
        if (isCancelled) {
          return;
        }

        setSummary(data);

        if (!data) {
          setError("No summary file is available for this book.");
          setActiveChapterId(null);
          setActiveSectionId(null);
          return;
        }

        const firstChapter = data.chapters?.[0] || null;
        if (!firstChapter) {
          setActiveChapterId(null);
          setActiveSectionId(null);
          return;
        }

        const firstChapterId = resolveChapterId(firstChapter, 0);
        const firstSection = firstChapter.sections?.[0] || null;
        const firstSectionId = firstSection ? resolveSectionId(firstSection, 0) : null;
        const firstChapterHeading = getChapterHeading(firstChapter.chapter);

        setActiveChapterId(firstChapterId);
        setActiveSectionId(firstSectionId);

        if (firstSection) {
          const startLine = Number(firstSection?.source_location?.start_line);
          const endLine = Number(firstSection?.source_location?.end_line);
          const sectionTitle = firstSection.title || `Section ${firstSection.order || 1}`;

          if (!Number.isInteger(startLine) || !Number.isInteger(endLine)) {
            setSourceView({
              open: true,
              loading: false,
              error: "This item does not include a valid source line range.",
              data: null,
              chapterTitle: firstChapterHeading,
              sectionTitle,
            });
          } else {
            setSourceView({
              open: true,
              loading: true,
              error: "",
              data: null,
              chapterTitle: firstChapterHeading,
              sectionTitle,
            });

            try {
              const sourceData = await fetchBookSummarySource(readingKey, {
                startLine,
                endLine,
                sourceFile: firstSection?.source_location?.source_file || "book.md",
              });

              if (!isCancelled) {
                setSourceView((prev) => ({
                  ...prev,
                  loading: false,
                  data: sourceData,
                }));
              }
            } catch (sourceErr) {
              if (!isCancelled) {
                setSourceView((prev) => ({
                  ...prev,
                  loading: false,
                  error: sourceErr.message || "Failed to load source excerpt",
                }));
              }
            }
          }
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message || "Failed to load summary");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadSummary();
    return () => {
      isCancelled = true;
    };
  }, [readingKey]);

  const chapters = summary?.chapters || [];

  const activeChapterIndex = chapters.findIndex((chapter, index) => resolveChapterId(chapter, index) === activeChapterId);
  const activeChapter = activeChapterIndex >= 0 ? chapters[activeChapterIndex] : chapters[0] || null;
  const activeChapterHeading = activeChapter ? getChapterHeading(activeChapter.chapter) : "";

  const activeSections = activeChapter?.sections || [];

  const activeSectionIndex = activeSections.findIndex((section, index) => resolveSectionId(section, index) === activeSectionId);
  const activeSection = activeSectionIndex >= 0 ? activeSections[activeSectionIndex] : activeSections[0] || null;

  const selectChapter = (chapter, chapterIndex) => {
    const chapterId = resolveChapterId(chapter, chapterIndex);
    const firstSection = chapter.sections?.[0] || null;
    const chapterHeading = getChapterHeading(chapter.chapter);

    setActiveChapterId(chapterId);
    setActiveSectionId(firstSection ? resolveSectionId(firstSection, 0) : null);

    if (firstSection) {
      openSourceForLocation({
        location: firstSection.source_location,
        chapterTitle: chapterHeading,
        sectionTitle: firstSection.title || `Section ${firstSection.order || 1}`,
      });
    } else {
      setSourceView(resetSourceView);
    }
  };

  const selectSection = async (section, sectionIndex) => {
    const sectionId = resolveSectionId(section, sectionIndex);
    setActiveSectionId(sectionId);

    if (sourceView.open) {
      await openSourceForLocation({
        location: section?.source_location,
        chapterTitle: activeChapterHeading,
        sectionTitle: section?.title || `Section ${section.order || sectionIndex + 1}`,
      });
    }
  };

  const openSourceForLocation = async ({ location, chapterTitle, sectionTitle }) => {
    const startLine = Number(location?.start_line);
    const endLine = Number(location?.end_line);

    if (!Number.isInteger(startLine) || !Number.isInteger(endLine)) {
      setSourceView({
        open: true,
        loading: false,
        error: "This item does not include a valid source line range.",
        data: null,
        chapterTitle,
        sectionTitle,
      });
      return;
    }

    setSourceView({
      open: true,
      loading: true,
      error: "",
      data: null,
      chapterTitle,
      sectionTitle,
    });

    try {
      const data = await fetchBookSummarySource(readingKey, {
        startLine,
        endLine,
        sourceFile: location?.source_file || "book.md",
      });

      setSourceView((prev) => ({
        ...prev,
        loading: false,
        data,
      }));
    } catch (err) {
      setSourceView((prev) => ({
        ...prev,
        loading: false,
        error: err.message || "Failed to load source excerpt",
      }));
    }
  };

  const openSourceForActiveSection = async () => {
    if (!activeSection) {
      return;
    }

    await openSourceForLocation({
      location: activeSection.source_location,
      chapterTitle: activeChapterHeading,
      sectionTitle: activeSection.title || "Section",
    });
  };

  const closeSourcePane = () => {
    setSourceView(resetSourceView);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ background: "var(--background)" }}>
        <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: "var(--card-border)", borderTopColor: "var(--accent)" }} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ background: "var(--background)" }}>
      <header className="px-5 sm:px-7 py-3 border-b backdrop-blur-md" style={{ borderColor: "var(--card-border)", background: "var(--card)" }}>
        <div className="flex items-center justify-between gap-3">
          <Link
            to={`/reading/${readingKey}/read`}
            className="inline-flex items-center gap-1 text-sm font-medium hover:underline whitespace-nowrap"
            style={{ color: "var(--accent)" }}
          >
            <HiOutlineChevronLeft className="w-4 h-4" aria-hidden="true" />
            Back
          </Link>
          <h1 className="text-base sm:text-lg font-semibold tracking-tight truncate" style={{ color: "var(--foreground)" }}>
            {summary?.book?.title || "Book Summary"}
          </h1>
          <div className="w-10" aria-hidden="true" />
        </div>
      </header>

      <div className="flex-1 min-h-0 p-4 sm:p-5">
        {error ? (
          <div className="notice-error h-full rounded-2xl p-4 text-base">
            {error}
          </div>
        ) : (
          <div className="h-full min-h-0 grid gap-4 lg:grid-cols-[280px_320px_minmax(0,1fr)]">
            <aside className="surface-card min-h-0 rounded-2xl flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b" style={{ borderColor: "var(--card-border)" }}>
                <h2 className="text-sm font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--foreground-muted)" }}>Chapters</h2>
              </div>
              <div className="flex-1 min-h-0 overflow-auto p-2.5 space-y-1.5">
                {chapters.map((chapter, chapterIndex) => {
                  const chapterId = resolveChapterId(chapter, chapterIndex);
                  const isActive = chapterId === resolveChapterId(activeChapter, activeChapterIndex >= 0 ? activeChapterIndex : 0);
                  const chapterHeading = getChapterHeading(chapter.chapter);
                  const sectionCount = chapter.sections?.length || 0;

                  return (
                    <ListPaneButton key={chapterId} isActive={isActive} onClick={() => selectChapter(chapter, chapterIndex)}>
                      <p className="text-xs font-semibold uppercase tracking-[0.12em] opacity-70">
                        Chapter {chapter.order || chapterIndex + 1}
                      </p>
                      <p className="mt-1 text-base font-semibold leading-6">{chapterHeading}</p>
                      <p className="mt-1 text-sm opacity-75">{sectionCount} sections</p>
                    </ListPaneButton>
                  );
                })}
              </div>
            </aside>

            <aside className="surface-card min-h-0 rounded-2xl flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b" style={{ borderColor: "var(--card-border)" }}>
                <h2 className="text-sm font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--foreground-muted)" }}>Sections</h2>
                {activeChapter && (
                  <p className="mt-1 text-sm font-medium line-clamp-2" style={{ color: "var(--foreground)" }}>{activeChapterHeading}</p>
                )}
              </div>
              <div className="flex-1 min-h-0 overflow-auto p-2.5 space-y-1.5">
                {activeSections.length === 0 && (
                  <p className="px-3 py-2 text-sm" style={{ color: "var(--foreground-muted)" }}>No sections in this chapter.</p>
                )}

                {activeSections.map((section, sectionIndex) => {
                  const sectionId = resolveSectionId(section, sectionIndex);
                  const isActive = sectionId === resolveSectionId(activeSection, activeSectionIndex >= 0 ? activeSectionIndex : 0);
                  const fallbackTitle = `Section ${section.order || sectionIndex + 1}`;

                  return (
                    <ListPaneButton key={sectionId} isActive={isActive} onClick={() => selectSection(section, sectionIndex)}>
                      <p className="text-base font-semibold leading-6">{section.title || fallbackTitle}</p>
                      {section.study_notes?.main_idea && (
                        <p className="mt-1 text-sm opacity-75 line-clamp-2">{section.study_notes.main_idea}</p>
                      )}
                    </ListPaneButton>
                  );
                })}
              </div>
            </aside>

            <section className="min-h-0">
              <div className={`h-full min-h-0 grid gap-4 ${sourceView.open ? "grid-cols-1 2xl:grid-cols-[minmax(360px,0.95fr)_minmax(0,1.2fr)]" : "grid-cols-1"}`}>
                <article className="surface-card min-h-0 rounded-2xl flex flex-col overflow-hidden">
                  <div className="px-5 py-4 border-b flex flex-wrap items-start justify-between gap-3" style={{ borderColor: "var(--card-border)" }}>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--foreground-muted)" }}>Section Details</p>
                      <h2 className="mt-1 text-2xl font-semibold" style={{ color: "var(--foreground)" }}>
                        {activeSection?.title || "Select a section"}
                      </h2>
                      {activeChapterHeading && (
                        <p className="mt-1 text-sm" style={{ color: "var(--foreground-muted)" }}>{activeChapterHeading}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={sourceView.open ? closeSourcePane : openSourceForActiveSection}
                        disabled={!sourceView.open && !activeSection?.source_location}
                        className="inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-semibold disabled:cursor-not-allowed"
                        style={
                          !sourceView.open && !activeSection?.source_location
                            ? { color: "var(--foreground-muted)", opacity: 0.6 }
                            : sourceView.open
                              ? { background: "var(--accent-tint)", color: "var(--accent)" }
                              : { color: "var(--accent)" }
                        }
                      >
                        {sourceView.open ? "Hide Original Text" : "Show Original Text"}
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-auto p-5 space-y-5">
                    {!activeSection && (
                      <p className="text-base" style={{ color: "var(--foreground-muted)" }}>Choose a section from the middle panel.</p>
                    )}

                    {activeSection && (
                      <>
                        {activeSection.study_notes?.main_idea && (
                          <p className="text-lg leading-8" style={{ color: "var(--foreground)" }}>
                            {activeSection.study_notes.main_idea}
                          </p>
                        )}

                        <SummaryList title="Key Points" items={activeSection.study_notes?.key_points} />
                        <SummaryList title="Examples" items={activeSection.study_notes?.examples} />
                        <SummaryList title="Tips" items={activeSection.study_notes?.tips} />

                        {Array.isArray(activeSection.study_notes?.important_terms) && activeSection.study_notes.important_terms.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--foreground-muted)" }}>Important Terms</p>
                            <div className="space-y-2">
                              {activeSection.study_notes.important_terms.map((term, termIndex) => (
                                <p key={`${resolveSectionId(activeSection, activeSectionIndex)}-term-${termIndex}`} className="text-base leading-7" style={{ color: "var(--foreground-muted)" }}>
                                  <span className="font-semibold" style={{ color: "var(--foreground)" }}>{term.term}:</span>{" "}
                                  {term.definition}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                        {activeSection.study_notes?.takeaway && (
                          <p className="text-base leading-7 pl-3 border-l-2" style={{ color: "var(--accent)", borderColor: "color-mix(in srgb, var(--accent) 50%, transparent)" }}>
                            <span className="font-semibold">Takeaway:</span> {activeSection.study_notes.takeaway}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </article>

                {sourceView.open && (
                  <article className="surface-card min-h-0 rounded-2xl flex flex-col overflow-hidden">
                    <div className="px-5 py-4 border-b" style={{ borderColor: "var(--card-border)" }}>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--foreground-muted)" }}>Original Text</p>
                      <h3 className="mt-1 text-xl font-semibold" style={{ color: "var(--foreground)" }}>{sourceView.sectionTitle || "Source"}</h3>
                      <p className="text-sm" style={{ color: "var(--foreground-muted)" }}>{sourceView.chapterTitle}</p>
                      {sourceView.data && (
                        <p className="mt-1 text-sm" style={{ color: "var(--foreground-muted)" }}>
                          {sourceView.data.source_file} lines {sourceView.data.start_line}-{sourceView.data.end_line}
                        </p>
                      )}
                    </div>

                    <div className="flex-1 min-h-0 overflow-auto p-5">
                      {sourceView.loading && (
                        <div className="h-full flex items-center justify-center">
                          <div className="w-7 h-7 border-4 rounded-full animate-spin" style={{ borderColor: "var(--card-border)", borderTopColor: "var(--accent)" }} />
                        </div>
                      )}

                      {!sourceView.loading && sourceView.error && (
                        <div className="notice-error rounded-lg p-3 text-sm">
                          {sourceView.error}
                        </div>
                      )}

                      {!sourceView.loading && !sourceView.error && (
                        <>
                          {sourceView.data?.content ? (
                            <div className={`prose prose-base max-w-none prose-headings:tracking-tight prose-p:leading-8 prose-li:leading-8 ${mode === "dark" ? "prose-invert" : ""}`}>
                              <ReactMarkdown>{sourceView.data.content}</ReactMarkdown>
                            </div>
                          ) : (
                            <p className="text-base" style={{ color: "var(--foreground-muted)" }}>No source text is available for this range.</p>
                          )}
                        </>
                      )}
                    </div>
                  </article>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default Summary;
